/**
 * Where a notification points, as a route THIS client can actually open.
 *
 * A stored deeplink is a LOGICAL path, not a URL — `orders`, `orders/1043`, `invoices/77`. The
 * server-owned catalogue (backend `notification-links.ts`) records, per target, the browser route
 * and the Android route, precisely so each client can resolve the logical path into its own route
 * table. Android does that in `core/push/Deeplinks.kt`. The web never did: the bell prefixed a
 * slash and navigated, so `invoices/77` became `/invoices/77` — which is not a route — and
 * `expenses` became `/expenses` when the page is `/expense-reports`. Seventeen of the catalogue's
 * targets resolved to the not-found page, including every one the broadcast composer offers under
 * Billing, Field force and Inventory.
 *
 * Two shapes arrive here and they are told apart by the leading slash:
 *   • `/billing/invoices/77` — a WEB PATH, written by a domain trigger in the dispatcher. Already
 *     a route; passed through untouched, query string and all.
 *   • `invoices/77` — a LOGICAL CODE, written by the broadcast composer. `campaign.controller`
 *     strips leading slashes when storing one, so the discriminator is reliable.
 */

/** Logical code → browser route, mirroring the backend catalogue. `null` = no web page exists. */
export const NOTIFY_WEB_PATHS: Record<string, string | null> = {
  // General
  '': '/home',
  notifications: null, // the web bell IS the inbox; there is no page to open
  profile: '/profile',
  // Orders
  orders: '/orders',
  'orders/{id}': '/orders/{id}',
  'orders/approval': '/orders/approval',
  // Billing
  invoices: '/billing/invoices',
  'invoices/{id}': '/billing/invoices/{id}',
  payments: '/billing/payments',
  aging: '/billing/aging',
  // Catalogue
  products: '/products',
  shop: '/products',
  catalog: null, // farmer catalogue: app-only
  brochures: null, // app-only
  schemes: '/masters/schemes',
  // Complaints
  complaints: '/complaints',
  'complaints/{id}': '/complaints/{id}',
  // Field force
  attendance: '/ffa/attendance',
  visits: '/ffa/visits',
  myday: '/ffa/my-day',
  expenses: '/expense-reports',
  // Inventory
  stock: '/inventory/stock',
  transfers: '/inventory/transfers',
  lowStock: '/inventory/low-stock',
  // Events
  events: '/events',
  'events/{id}': '/events/{id}',
};

/**
 * Where a target with no web page sends a browser reader.
 *
 * A farmer's catalogue and the app's notification inbox exist only in the app, and the
 * acknowledgement of a farmer's own enquiry is sent to both. Home is the honest answer: the reader
 * is signed in and lands somewhere real, instead of on the not-found page.
 */
const NO_WEB_PAGE = '/home';

/**
 * Web paths that were written wrong, and where they were always meant to go.
 *
 * A notification stores its link at SEND time, so correcting a trigger only helps the next one: the
 * ones already sitting unread in everybody's bell still carry the path that was wrong when they
 * were written. Repairing them here rather than by migration means no stored row has to be
 * rewritten, and a client that is behind still resolves them the old way rather than breaking.
 *
 * Ordered, first match wins. Only ever ADD to this list — an entry is a promise about links that
 * have already been sent, and deleting one un-breaks nothing.
 */
const LEGACY_WEB_PATHS: Array<[RegExp, string]> = [
  [/^\/invoices\/([^/?#]+)$/, '/billing/invoices/$1'],
  // The payment id cannot address a row (the register focuses on paymentNo), so the register it is.
  [/^\/payments\/[^/?#]+$/, '/billing/payments?tab=list'],
  [/^\/advances\/([^/?#]+)$/, '/wallets?view=requests&focus=$1'],
  [/^\/expenses$/, '/expense-reports'],
  [/^\/inquiries\/([^/?#]+)$/, '/inquiries?open=$1'],
  [/^\/route$/, '/ffa/my-day'],
  [/^\/field\/movement\?user=([^&]+)$/, '/performance?rep=$1'],
  // The farmer catalogue is app-only; on the web the reader lands on their own home instead.
  [/^\/catalog$/, NO_WEB_PAGE],
];

function repairLegacy(webPath: string, data?: Record<string, any> | null): string {
  /* The transfer triggers named no transfer at all — but each message stored the transfer NUMBER
     beside its link, and the register focuses a row on either its id or its number. So the ones
     already sitting in the bell can still open the transfer they were sent about. */
  if (/^\/inventory\/transfers$/.test(webPath) && data?.transferNo) {
    return `/inventory/master-stock?tab=transfers&focus=${encodeURIComponent(String(data.transferNo))}`;
  }
  for (const [re, to] of LEGACY_WEB_PATHS) if (re.test(webPath)) return webPath.replace(re, to);
  return webPath;
}

/**
 * Links to a screen the reader may not be allowed to open, and the screen such a reader opens instead.
 *
 * An approval lane has ONE link — the approver's view of the record — and the decision is sent to the person who
 * RAISED the record with that same link (dispatcher `queueDecision`; `approvalReverted` too). For an attendance
 * regularization that is the review tab under Attendance, which needs ATTENDANCE.VIEW. A Sales Person does not hold
 * it, so "Attendance regularization approved" bounced him to his dashboard, which says nothing about the decision
 * (DEF-071, 15 Sep). His own requests — status, reviewer, note — are on My Field Day, which takes the same `focus`.
 *
 * Applied on READ, like the repairs above, so the decisions already sitting in people's bells open correctly too. A
 * reader who holds the permission keeps the original link.
 */
const FOR_READERS_WITHOUT: Array<{ path: RegExp; permission: string; to: string }> = [
  { path: /^\/ffa\/attendance\?tab=reg&focus=([^&#]+)$/, permission: 'ATTENDANCE.VIEW', to: '/ffa/my-day?focus=$1' },
];

function forReader(webPath: string, can: (permission: string) => boolean): string {
  for (const r of FOR_READERS_WITHOUT) if (r.path.test(webPath) && !can(r.permission)) return webPath.replace(r.path, r.to);
  return webPath;
}


/**
 * Resolve a stored notification target to a browser route, or null when it names nothing.
 *
 * An unrecognised code still falls through to `/<code>` rather than being dropped. The composer
 * validates against the catalogue, so this only catches rows written before it did — and a stale
 * guess is a better outcome for them than a bell entry that refuses to open at all.
 */
export function resolveNotifyLink(
  raw: string | null | undefined,
  data?: Record<string, any> | null,
  /** The reader's permission check. When given, a link the reader cannot open becomes the one they can (see above). */
  can?: (permission: string) => boolean,
): string | null {
  const route = resolveRoute(raw, data);
  return route && can ? forReader(route, can) : route;
}

function resolveRoute(raw: string | null | undefined, data?: Record<string, any> | null): string | null {
  const value = String(raw ?? '').trim();
  if (!value) return null;
  if (value.startsWith('/')) return repairLegacy(value, data); // already a web path

  const [code, query] = splitQuery(value);
  const withQuery = (path: string) => (query ? `${path}?${query}` : path);

  // Exact first: `orders/approval` is a target in its own right and must not be read as an id.
  if (code in NOTIFY_WEB_PATHS) {
    const path = NOTIFY_WEB_PATHS[code];
    return withQuery(path ?? NO_WEB_PAGE);
  }

  // Then as `<head>/{id}` — `invoices/77` is the catalogue's `invoices/{id}`.
  const cut = code.lastIndexOf('/');
  if (cut > 0) {
    const template = `${code.slice(0, cut)}/{id}`;
    const id = code.slice(cut + 1);
    if (id && template in NOTIFY_WEB_PATHS) {
      const path = NOTIFY_WEB_PATHS[template];
      return withQuery(path ? path.replace('{id}', id) : NO_WEB_PAGE);
    }
  }

  return withQuery(`/${code}`);
}

function splitQuery(value: string): [string, string] {
  const at = value.indexOf('?');
  return at < 0 ? [value, ''] : [value.slice(0, at), value.slice(at + 1)];
}
